import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
    Box,
    CircularProgress,
} from '@mui/material';

import {
    DataGridPro,
    GridToolbar,
    GridColDef,
    GridRowId
} from '@mui/x-data-grid-pro';

import { useOutletContext } from 'react-router-dom';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { toast } from 'react-toastify';
import axios from 'axios';

import { socket } from '../services/socket';

interface CommissionsReportRow {
    id: GridRowId;
    col0: string;
    col1: string;
    col2: string;
    col3: number | null;
    col4: number | null;
    col5: number;
    col6: boolean;
    col7?: unknown;
}

interface OutletContextType {
    isConnected: boolean;
    maestroPermissions: string[];
}

interface EditedRowMap {
    [key: string]: CommissionsReportRow;
}

const api = axios.create({
    withCredentials: true
});

const CommissionsReport: React.FC = () => {
    const { isConnected, maestroPermissions } =
        useOutletContext<OutletContextType>();

    const { getAccessTokenSilently } = useAuth0();

    const [commissionsRows, setCommissionsRows] = useState<CommissionsReportRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isAuthorized = maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Read`) &&
        maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Commissions:Update`)

    async function getCommissionsReportData() {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            /** const response = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const r: CommissionsReportRow[] = response.data.map((row: any) => ({
                id: row.vendorId,
                col0: row.companyName,
                col1: row.email,
                col2: row.division,
                col3: formatPercentDisplay(
                    row.commissionsPercentageOverRevenue
                ),
                col4: formatPercentDisplay(
                    row.commissionsPercentageOverProfit
                ),
                col5: row.commissionsPayOn,
                col6: row.commissionsShouldCalculate
            }));

            setCommissionsRows(r); **/
        } catch (err: any) {
            if (err.message !== 'canceled') {
                toast.error(
                    err?.response?.data?.message || 'Failed to fetch commissions report data.'
                );
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isConnected && isAuthorized) {
            getCommissionsReportData();
            socket.on('commissions', getCommissionsReportData);
        }

        return () => {
            socket.off('commissions');
        };
    }, [isConnected]);

    const columns = useMemo<GridColDef<CommissionsReportRow>[]>(
        () => [
            { field: 'col0', headerName: 'Placeholder 1', flex: 0.25, minWidth: 100 },
            { field: 'col1', headerName: 'Placeholder 2', flex: 0.25, minWidth: 120 },
            { field: 'col2', headerName: 'Placeholder 3', flex: 0.1, minWidth: 150 },
            {
                field: 'col3',
                headerName: 'Placeholder 4',
                flex: 0.1,
                minWidth: 150,
            },
            {
                field: 'col4',
                headerName: 'Placeholder 5',
                flex: 0.1,
                minWidth: 150,
            },
            {
                field: 'col5',
                headerName: 'Placeholder 6',
                flex: 0.1,
                minWidth: 120,
            },
            {
                field: 'col6',
                headerName: 'Placeholder 7',
                flex: 0.12,
                minWidth: 100,
            },
        ],
        [commissionsRows]
    );

    if (isAuthorized) 
        {
        return (
            <Box sx={{ p: 3 }}>
                <div style={{ height: '80vh', width: '100%' }}>
                    <DataGridPro
                        rows={commissionsRows}
                        columns={columns}
                        loading={isLoading}
                        pagination
                        slots={{
                            toolbar: GridToolbar,
                            loadingOverlay: () => (
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500
                                }
                            }
                        }}
                    />
                </div>
            </Box>
        );
    }
    return (
        <Box sx={{ textAlign: 'center', p: 3 }}>
            <WarningRoundedIcon />
            <p>Not Authorized</p>
        </Box>
    );
};

export default CommissionsReport;